/** @module The live log: pick a service, filter by level or text, follow it or hold it still. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LogView } from "@/features/admin/log-view";
import { type Level, LEVELS, useLogs } from "@/features/admin/use-admin";

const SIZES = [100, 200, 500] as const;

export function LogsPanel() {
  const { t } = useTranslation();
  const [service, setService] = useState("api");
  const [level, setLevel] = useState<Level | undefined>(undefined);
  const [contains, setContains] = useState("");
  const [lines, setLines] = useState<number>(200);
  const [live, setLive] = useState(true);

  const logs = useLogs({ service, lines, level, contains: contains.trim() || undefined, live });
  const services = logs.data?.services ?? [service];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[length:var(--text-sm)]">
          <span className="t-muted">{t("admin.logs.service")}</span>
          <select
            value={logs.data?.service ?? service}
            onChange={(event) => {
              setService(event.target.value);
            }}
            className="input"
          >
            {services.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[length:var(--text-sm)]">
          <span className="t-muted">{t("admin.logs.level")}</span>
          <select
            value={level ?? ""}
            onChange={(event) => {
              setLevel((event.target.value || undefined) as Level | undefined);
            }}
            className="input"
          >
            <option value="">{t("admin.logs.anyLevel")}</option>
            {LEVELS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-48 grow flex-col gap-1 text-[length:var(--text-sm)]">
          <span className="t-muted">{t("admin.logs.search")}</span>
          <input
            type="search"
            value={contains}
            onChange={(event) => {
              setContains(event.target.value);
            }}
            placeholder={t("admin.logs.searchHint")}
            className="input"
          />
        </label>

        <label className="flex flex-col gap-1 text-[length:var(--text-sm)]">
          <span className="t-muted">{t("admin.logs.lines")}</span>
          <select
            value={lines}
            onChange={(event) => {
              setLines(Number(event.target.value));
            }}
            className="input"
          >
            {SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          aria-pressed={live}
          onClick={() => {
            setLive((on) => !on);
          }}
          className="min-h-(--touch) rounded-[var(--radius-pill)] border border-(--border) px-4 text-[length:var(--text-sm)] aria-pressed:border-(--accent) aria-pressed:bg-(--accent-soft) aria-pressed:text-(--accent-soft-ink)"
        >
          {live ? t("admin.logs.following") : t("admin.logs.paused")}
        </button>
      </div>

      {logs.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      <LogView lines={logs.data?.lines ?? []} empty={t("admin.logs.empty")} />
    </div>
  );
}
